import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import { Button } from "@/shared/components/ui/button";
import { useToast } from "@/shared/hooks/use-toast";
import { SettingsService, SettingItem } from "@/shared/services/api/SettingsService";

interface EmailNotificationsValue {
  meeting_confirmation: boolean;
  meeting_cancellation: boolean;
  meeting_error: boolean;
  api_token_created: boolean;
  api_token_expiring: boolean;
  api_token_expired: boolean;
  api_token_revoked: boolean;
  invite: boolean;
  already_registered: boolean;
  meeting_scriber_welcome: boolean;
}

const DEFAULT_VALUE: EmailNotificationsValue = {
  meeting_confirmation: true,
  meeting_cancellation: true,
  meeting_error: true,
  api_token_created: true,
  api_token_expiring: true,
  api_token_expired: true,
  api_token_revoked: true,
  invite: true,
  already_registered: true,
  meeting_scriber_welcome: true,
};

export const EmailNotificationsSettings = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [currentSetting, setCurrentSetting] = useState<SettingItem | null>(null);
  const [formValue, setFormValue] = useState<EmailNotificationsValue>(DEFAULT_VALUE);

  const { data, isLoading } = useQuery({
    queryKey: ["email_notifications_settings"],
    queryFn: () => SettingsService.getRawSettingByType("email_notifications"),
  });

  useEffect(() => {
    if (data) {
      setCurrentSetting(data);
      const value = (data.value || {}) as Partial<EmailNotificationsValue>;
      setFormValue({
        meeting_confirmation: value.meeting_confirmation ?? true,
        meeting_cancellation: value.meeting_cancellation ?? true,
        meeting_error: value.meeting_error ?? true,
        api_token_created: value.api_token_created ?? true,
        api_token_expiring: value.api_token_expiring ?? true,
        api_token_expired: value.api_token_expired ?? true,
        api_token_revoked: value.api_token_revoked ?? true,
        invite: value.invite ?? true,
        already_registered: value.already_registered ?? true,
        meeting_scriber_welcome: value.meeting_scriber_welcome ?? true,
      });
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: async (payload: EmailNotificationsValue) => {
      if (!currentSetting) throw new Error("Setting not loaded");
      return SettingsService.updateSetting(currentSetting.id, { value: payload });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email_notifications_settings"] });
      toast({
        title: t("settings.emailNotifications.saveSuccess"),
        variant: "default",
      });
    },
    onError: (error: unknown) => {
      toast({
        title: t("common.error"),
        description:
          error instanceof Error
            ? error.message
            : t("settings.emailNotifications.saveError"),
        variant: "destructive",
      });
    },
  });

  const handleToggle = (field: keyof EmailNotificationsValue, checked: boolean) => {
    setFormValue((prev) => ({ ...prev, [field]: checked }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formValue);
  };

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{t("settings.emailNotifications.title")}</CardTitle>
          <CardDescription>{t("settings.emailNotifications.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        </CardContent>
      </Card>
    );
  }

  if (!currentSetting) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{t("settings.emailNotifications.title")}</CardTitle>
          <CardDescription>{t("settings.emailNotifications.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t("settings.emailNotifications.missingConfig")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const groups: {
    groupKey: string;
    items: { field: keyof EmailNotificationsValue; labelKey: string; descriptionKey: string }[];
  }[] = [
    {
      groupKey: "settings.emailNotifications.groupAssistant",
      items: [
        {
          field: "meeting_confirmation",
          labelKey: "settings.emailNotifications.meetingConfirmation",
          descriptionKey: "settings.emailNotifications.meetingConfirmationDescription",
        },
        {
          field: "meeting_cancellation",
          labelKey: "settings.emailNotifications.meetingCancellation",
          descriptionKey: "settings.emailNotifications.meetingCancellationDescription",
        },
        {
          field: "meeting_error",
          labelKey: "settings.emailNotifications.meetingError",
          descriptionKey: "settings.emailNotifications.meetingErrorDescription",
        },
      ],
    },
    {
      groupKey: "settings.emailNotifications.groupApiTokens",
      items: [
        {
          field: "api_token_created",
          labelKey: "settings.emailNotifications.apiTokenCreated",
          descriptionKey: "settings.emailNotifications.apiTokenCreatedDescription",
        },
        {
          field: "api_token_expiring",
          labelKey: "settings.emailNotifications.apiTokenExpiring",
          descriptionKey: "settings.emailNotifications.apiTokenExpiringDescription",
        },
        {
          field: "api_token_expired",
          labelKey: "settings.emailNotifications.apiTokenExpired",
          descriptionKey: "settings.emailNotifications.apiTokenExpiredDescription",
        },
        {
          field: "api_token_revoked",
          labelKey: "settings.emailNotifications.apiTokenRevoked",
          descriptionKey: "settings.emailNotifications.apiTokenRevokedDescription",
        },
      ],
    },
    {
      groupKey: "settings.emailNotifications.groupUserAccount",
      items: [
        {
          field: "invite",
          labelKey: "settings.emailNotifications.invite",
          descriptionKey: "settings.emailNotifications.inviteDescription",
        },
        {
          field: "already_registered",
          labelKey: "settings.emailNotifications.alreadyRegistered",
          descriptionKey: "settings.emailNotifications.alreadyRegisteredDescription",
        },
        {
          field: "meeting_scriber_welcome",
          labelKey: "settings.emailNotifications.meetingScriberWelcome",
          descriptionKey: "settings.emailNotifications.meetingScriberWelcomeDescription",
        },
      ],
    },
  ];

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t("settings.emailNotifications.title")}</CardTitle>
        <CardDescription>{t("settings.emailNotifications.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {groups.map((group) => (
            <div key={group.groupKey} className="space-y-3">
              <p className="font-semibold text-sm">{t(group.groupKey)}</p>
              <div className="rounded-md border divide-y">
                {group.items.map(({ field, labelKey, descriptionKey }) => (
                  <div key={field} className="flex items-center justify-between gap-4 p-4">
                    <div className="space-y-1">
                      <Label htmlFor={field}>{t(labelKey)}</Label>
                      <p className="text-sm text-muted-foreground">{t(descriptionKey)}</p>
                    </div>
                    <Switch
                      id={field}
                      checked={formValue[field]}
                      onCheckedChange={(checked) => handleToggle(field, checked)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="flex justify-end">
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending
                ? t("common.saving", { defaultValue: "Saving..." })
                : t("common.save", { defaultValue: "Save" })}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
